import Vue from 'vue'
import VueRouter, { NavigationGuard, RouteConfig } from 'vue-router'
import ServiceReport from '../views/Reports/ServiceReport.vue'
import InvoiceReport from '../views/Reports/InvoiceReport.vue'
import PassengerReport from '../views/Reports/PassengerReport.vue'
import TravelsReport from '../views/Reports/TravelsReport.vue'
import AccountLogin from '../views/Account/Login.vue'
import ErrorPage from '../views/ErrorPage.vue'
import About from '../views/AboutView.vue'
import Dashboard from '../views/DashboardView.vue'
import store from '@/store'
import i18n from '@/i18n'
import BusinessInfoView from '@/views/InfoView.vue'
import MyFavourites from '@/views/MyFavourites.vue'
import BonuspointsView from '@/views/BonuspointsView.vue'

Vue.use(VueRouter)

const routes: Array<RouteConfig> = [
    {
        path: '/',
        redirect: `${i18n.locale}/dashboard`
    },
    {
        path: '/:lang(et|en)',
        component: {
            render (c) { return c('router-view') }
        },
        children: [
            { path: '/', redirect: `/${i18n.locale}/dashboard` },
            { path: 'reports', redirect: `/${i18n.locale}/reports/invoice-report` },
            {
                path: 'reports/service-report',
                name: 'Servicereport',
                component: ServiceReport,
                meta: { requiresCustomerType: 'O' }
            },
            {
                path: 'reports/invoice-report',
                name: 'Invoicereport',
                component: InvoiceReport,
                meta: { requiresCustomerType: 'O' }
            },
            {
                path: 'reports/passenger-report',
                name: 'Passengerreport',
                component: PassengerReport,
                meta: { requiresCustomerType: 'O' }
            },
            { path: 'reports/travels-report', name: 'Travelsreport', component: TravelsReport },
            { path: 'account/login', name: 'AccountLogin', component: AccountLogin, meta: { requiresGuest: true } },
            { path: 'reports/about', name: 'AboutPage', component: About, meta: { requiresCustomerType: 'O' } },
            { path: 'dashboard', name: 'Dashboard', component: Dashboard },
            { path: 'info', name: 'Info', component: BusinessInfoView },
            { path: 'favourites', name: 'Favourites', component: MyFavourites },
            { path: 'bonuspoints', name: 'Bonuspoints', component: BonuspointsView }
        ]
    },
    {
        // This route will catch any undefined routes and redirect to the error page
        path: '/:catchAll(.*)',
        name: 'PageNotFound',
        component: ErrorPage
    }
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
})

// Navigation guard to check authentication
const isAuthenticated: NavigationGuard = (to, from, next) => {
    const isAuthenticated = store.getters.isAuthenticated;
    if (isAuthenticated) {
        // User is authenticated, proceed to the next route

        // Check if customerType 'P' tries to access routes that are for customerType 'O'
        const customerType = store.getters.getCustomerType;
        if (to.meta.requiresCustomerType && to.meta.requiresCustomerType !== customerType) {
            // If customerType doesn't match, redirect to travels report
            return next({ name: 'Travelsreport', params: { lang: to.params.lang } });
        }
        next()
    } else {
        // User is not authenticated, redirect to login page
        if (from.name !== 'AccountLogin') { // If user was logged in but the session has expired
            Vue.notify({
                group: 'auth',
                title: i18n.t('notifications.session_expired.title') as string,
                text: i18n.t('notifications.session_expired.text') as string,
                type: 'warn'
            });
        }
        next({ name: 'AccountLogin', params: { lang: localStorage.getItem('lang') || to.params.lang } })
    }
}

// Apply the navigation guard to all routes except '/account/login'
router.beforeEach((to, from, next) => {
    let language = to.params.lang;
    if (language !== 'en' && language !== 'et') {
        language = localStorage.getItem('lang') || 'et';
    }
    to.params.lang = language;
    i18n.locale = language;
    if (to.name !== 'AccountLogin') {
        isAuthenticated(to, from, next)
    } else if (to.meta.requiresGuest && store.getters.isAuthenticated) {
        next({ path: '/', params: { lang: language } })
    } else {
        // For '/account/login' route, no authentication check is needed
        next()
    }
})

export default router
